import { Fragment } from "react";

/**
 * Lightweight markdown-ish renderer for legal pages.
 * Supports:
 *   ## Heading        -> <h2>
 *   ### Heading       -> <h3>
 *   - item            -> <li>
 *   blank line        -> paragraph break
 *   **bold**          -> <strong>
 *   [text](url)       -> <a>
 *   ---               -> <hr>
 */

const inline = (text) => {
  if (!text) return null;
  // Split by bold/link tokens while keeping them
  const tokens = [];
  let remaining = text;
  let idx = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    const linkMatch = remaining.match(/\[([^\]]+)\]\(([^)]+)\)/);
    let next = null;
    if (boldMatch && (!linkMatch || boldMatch.index < linkMatch.index)) {
      next = { kind: "bold", index: boldMatch.index, match: boldMatch[0], inner: boldMatch[1] };
    } else if (linkMatch) {
      next = { kind: "link", index: linkMatch.index, match: linkMatch[0], inner: linkMatch[1], url: linkMatch[2] };
    }
    if (!next) {
      if (remaining) tokens.push(<Fragment key={idx++}>{remaining}</Fragment>);
      break;
    }
    if (next.index > 0) tokens.push(<Fragment key={idx++}>{remaining.slice(0, next.index)}</Fragment>);
    if (next.kind === "bold") tokens.push(<strong key={idx++}>{next.inner}</strong>);
    if (next.kind === "link") {
      const isExternal = /^https?:\/\//.test(next.url) || next.url.startsWith("mailto:") || next.url.startsWith("tel:");
      tokens.push(
        <a
          key={idx++}
          href={next.url}
          target={isExternal && !next.url.startsWith("mailto:") && !next.url.startsWith("tel:") ? "_blank" : undefined}
          rel={isExternal ? "noopener noreferrer" : undefined}
        >
          {next.inner}
        </a>
      );
    }
    remaining = remaining.slice(next.index + next.match.length);
  }
  return tokens;
};

export const Markdown = ({ source }) => {
  if (!source) return null;
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  const nodes = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (trimmed === "") {
      i++;
      continue;
    }
    if (trimmed === "---") {
      nodes.push(<hr key={key++} />);
      i++;
      continue;
    }
    if (trimmed.startsWith("### ")) {
      nodes.push(<h3 key={key++}>{inline(trimmed.slice(4))}</h3>);
      i++;
      continue;
    }
    if (trimmed.startsWith("## ")) {
      nodes.push(<h2 key={key++}>{inline(trimmed.slice(3))}</h2>);
      i++;
      continue;
    }
    if (trimmed.startsWith("- ")) {
      const items = [];
      while (i < lines.length && lines[i].trim().startsWith("- ")) {
        items.push(lines[i].trim().slice(2));
        i++;
      }
      nodes.push(
        <ul key={key++}>
          {items.map((it, idx) => (
            <li key={idx}>{inline(it)}</li>
          ))}
        </ul>
      );
      continue;
    }
    // paragraph — combine consecutive non-empty, non-special lines
    const buf = [];
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !lines[i].trim().startsWith("## ") &&
      !lines[i].trim().startsWith("### ") &&
      !lines[i].trim().startsWith("- ") &&
      lines[i].trim() !== "---"
    ) {
      buf.push(lines[i]);
      i++;
    }
    const joined = buf.join("\n");
    // Convert single newlines to <br/> within a paragraph
    const parts = joined.split("\n");
    nodes.push(
      <p key={key++}>
        {parts.map((part, idx) => (
          <Fragment key={idx}>
            {inline(part)}
            {idx < parts.length - 1 && <br />}
          </Fragment>
        ))}
      </p>
    );
  }

  return <>{nodes}</>;
};

export default Markdown;
